import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SitemapEditorContent } from '@/pages/SitemapEditor';
import { DesignSystemEditor } from '@/components/brand/DesignSystemEditor';
import { FloatingHeader } from '@/components/brand/site-mapper/components/FloatingHeader';

interface Props {
  isGlobalDarkMode: boolean;
  setIsGlobalDarkMode: (dark: boolean) => void;
}

export const DesignStudioEditor: React.FC<Props> = ({ isGlobalDarkMode, setIsGlobalDarkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  // Determine initial mode based on URL
  const dsTabs = ['/colors', '/typography', '/browse', '/spacing', '/layouts', '/components'];
  const isDsRoute = dsTabs.some(tab => location.pathname.endsWith(tab));
  
  const [activeMode, setActiveMode] = useState<'sitemap' | 'design-system' | 'comment'>(isDsRoute ? 'design-system' : 'sitemap');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  if (!id) {
    return <div className="flex items-center justify-center h-full">Blueprint ID not provided</div>;
  }

  // When URL changes (e.g. from ds navigation), ensure activeMode is synced
  useEffect(() => {
    const isNowDsRoute = dsTabs.some(tab => location.pathname.endsWith(tab));
    if (isNowDsRoute && activeMode !== 'design-system') {
      setActiveMode('design-system');
    } else if (!isNowDsRoute && activeMode === 'design-system') {
      setActiveMode('sitemap');
    }
  }, [location.pathname]);

  const handleModeChange = (newMode: 'sitemap' | 'design-system' | 'comment') => {
    setActiveMode(newMode);
    if (newMode === 'design-system') {
      navigate(`/brand/blueprints/${id}/colors`);
    } else if (newMode === 'sitemap') {
      navigate(`/brand/blueprints/${id}`);
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      <div 
        style={{ display: (activeMode === 'sitemap' || activeMode === 'comment') ? 'flex' : 'none' }} 
        className="flex-1 w-full h-full"
      >
         <SitemapEditorContent 
           sitemapId={id}
           isGlobalDarkMode={isGlobalDarkMode}
           setIsGlobalDarkMode={setIsGlobalDarkMode}
           activeMode={activeMode}
           setActiveMode={setActiveMode}
           sidebarOpen={sidebarOpen}
           setSidebarOpen={setSidebarOpen}
         />
      </div>

      <div 
        style={{ display: activeMode === 'design-system' ? 'flex' : 'none' }}
        className="flex-1 w-full h-full"
      >
        <DesignSystemEditor 
          embedded={true} 
          dsIdOverride={id}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          headerContent={
            <FloatingHeader
              sidebarOpen={sidebarOpen}
              onToggleSidebar={() => setSidebarOpen(prev => !prev)}
              activeMode={activeMode}
              onChangeMode={handleModeChange}
              isGlobalDarkMode={isGlobalDarkMode}
              setIsGlobalDarkMode={setIsGlobalDarkMode}
              backPath="/brand/blueprints"
            />
          }
        />
      </div>
    </div>
  );
};
